import { useState, useEffect } from 'react';
import { ICar, ITechnikBase } from '../../types/type';
import Input from '../Input/Input';
import ManyPhotoContainer from '../ManyPhotoContainer/ManyPhotoContainer';
import { fullName, toBackendDate, normalizeImageUrl } from '../../utils/utils';
import { formatDateForInput } from '../../utils/date.utils';
import { TechnicalService, CarUpdateData } from '../../services/technical.service';
import { UserService } from '../../services/user.service';
import DropDownInput from '../DropDownInput/DropDownInput';

function AutoCard({ car, carLength }: { car: ICar; carLength: number }) {
  const carPhotos = {
    frontPhoto: car.carImages.find((image) => image.photoType === 'front')?.photoUrl || '',
    rightPhoto: car.carImages.find((image) => image.photoType === 'right')?.photoUrl || '',
    backPhoto: car.carImages.find((image) => image.photoType === 'back')?.photoUrl || '',
    leftPhoto: car.carImages.find((image) => image.photoType === 'left')?.photoUrl || '',
    trunkPhoto: car.carImages.find((image) => image.photoType === 'trunk')?.photoUrl || '',
    carID: car.id,
  };

  // Состояния для углов поворота каждой фотографии
  const [rotations, setRotations] = useState<Record<string, number>>({
    front: 0,
    right: 0,
    back: 0,
    left: 0,
    trunk: 0,
  });

  const [gosNumber, setGosNumber] = useState(car.number);
  const [mark, setMark] = useState(car.mark || '');
  const [model, setModel] = useState(car.model || '');
  const [liciens, setLiciens] = useState(car.taxiLicense);
  const [liciensDate, setLiciensDate] = useState(car.issueDate);

  // Новые состояния для диагностической карты и техника
  const [diagnosticCardNumber, setDiagnosticCardNumber] = useState(car.diagnosticCardNumber?.toString() || '');
  const [validityPeriodOfDC, setValidityPeriodOfDC] = useState(car.validityPeriodOfDC || '');
  const [diagnosticCardVerified, setDiagnosticCardVerified] = useState(car.diagnosticCardVerified || false);
  // «Фамилия Имя Отчество» техника автосогласования
  const [technikName, setTechnikName] = useState(fullName(car.autonegotiationTechnik));
  const [technikId, setTechnikId] = useState(car.autonegotiationTechnik?.id || '');
  const [autonegotiation, setAutonegotiation] = useState(car.autonegotiation || false);

  const [previousDiagnosticCardNumber, setPreviousDiagnosticCardNumber] = useState(
    car.diagnosticCardNumber?.toString() || '',
  );
  const [previousValidityPeriodOfDC, setPreviousValidityPeriodOfDC] = useState(car.validityPeriodOfDC || '');
  const [previousTechnikId, setPreviousTechnikId] = useState(car.autonegotiationTechnik?.id || '');

  const [editable, setEditable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  // Список техников для выпадающего списка
  const [technicians, setTechnicians] = useState<{ text: string; value: string }[]>([]);
  const [isTechniciansLoading, setIsTechniciansLoading] = useState(false);

  const [errors, setErrors] = useState({
    gosNumber: '',
    mark: '',
    model: '',
    liciens: '',
    liciensDate: '',
    diagnosticCardNumber: '',
    validityPeriodOfDC: '',
  });

  // Загрузка списка техников
  useEffect(() => {
    const loadTechnicians = async () => {
      try {
        setIsTechniciansLoading(true);
        const response = await UserService.getTechnicians({ limit: 100, isActive: true });

        // Проверка структуры ответа и наличия данных
        if (response && Array.isArray(response.data) && response.data.length > 0) {
          const techItems = response.data
            .filter((tech) => tech.lastName || tech.firstName || tech.username) // Фильтрация только с именами
            .map((tech) => ({
              text:
                tech.lastName && tech.firstName
                  ? fullName(tech)
                  : `${tech.username || ''}${tech.email ? ` (${tech.email})` : ''}`,
              value: tech.id,
            }));

          // Если текущий техник не в списке, добавляем его
          if (technikId && technikName && !techItems.some((item) => item.value === technikId)) {
            techItems.push({
              text: technikName,
              value: technikId,
            });
          }

          // Сортируем по имени
          techItems.sort((a, b) => a.text.localeCompare(b.text));

          setTechnicians(techItems);
        } else {
          // Добавляем хотя бы текущего техника, если он есть
          if (technikId && technikName) {
            setTechnicians([
              {
                text: technikName,
                value: technikId,
              },
            ]);
          } else {
            setTechnicians([
              {
                text: 'Нет доступных техников',
                value: '',
              },
            ]);
          }
        }
      } catch (error) {
        // Добавляем хотя бы текущего техника при ошибке
        if (technikId && technikName) {
          setTechnicians([
            {
              text: technikName,
              value: technikId,
            },
          ]);
        }
      } finally {
        setIsTechniciansLoading(false);
      }
    };

    if (editable) {
      loadTechnicians();
    }
  }, [editable, technikId, technikName]);

  // Обновление предыдущих значений при загрузке
  useEffect(() => {
    setPreviousDiagnosticCardNumber(diagnosticCardNumber);
    setPreviousValidityPeriodOfDC(validityPeriodOfDC);
    setPreviousTechnikId(technikId);

    // Установка начального значения autonegotiation
    setAutonegotiation(car.autonegotiation || false);

    // Установка данных техника, если они есть
    if (car.autonegotiationTechnik && car.autonegotiationTechnik.id) {
      setTechnikId(car.autonegotiationTechnik.id);
      setTechnikName(fullName(car.autonegotiationTechnik));
    }
  }, []);

  // Проверка изменений в диагностической карте для сброса верификации
  useEffect(() => {
    if (
      editable &&
      (diagnosticCardNumber !== previousDiagnosticCardNumber || validityPeriodOfDC !== previousValidityPeriodOfDC)
    ) {
      setDiagnosticCardVerified(false);
    }
  }, [diagnosticCardNumber, validityPeriodOfDC, editable]);

  const validateGosNumber = (value: string) => {
    if (!value) return 'Гос номер обязателен.';
    return '';
  };

  const validateMark = (value: string) => {
    if (!value) return 'Марка авто обязательна.';
    return '';
  };

  const validateModel = (value: string) => {
    if (!value) return 'Модель авто обязательна.';
    return '';
  };

  const validateLiciens = (value: string) => {
    if (!value) return 'Лицензия обязательна.';
    if (!value.startsWith('№')) return 'Лицензия должна начинаться с символа №.';
    return '';
  };

  const validateLiciensDate = (value: string) => {
    if (!value) return 'Дата выдачи лицензии обязательна.';
    return '';
  };

  const validateDiagnosticCardNumber = (value: string) => {
    if (!value) return '';
    const numberValue = parseInt(value);
    if (isNaN(numberValue) || numberValue <= 0) return 'Введите корректный номер диагностической карты.';
    return '';
  };

  const validateValidityPeriodOfDC = (value: string) => {
    if (!value) return '';
    return '';
  };

  const handleGosNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setGosNumber(newValue);

    const error = validateGosNumber(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      gosNumber: error,
    }));
  };

  const handleMarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setMark(newValue);

    const error = validateMark(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      mark: error,
    }));
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setModel(newValue);

    const error = validateModel(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      model: error,
    }));
  };

  const handleLiciensChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLiciens(newValue);

    const error = validateLiciens(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      liciens: error,
    }));
  };

  const handleLiciensDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLiciensDate(newValue);

    const error = validateLiciensDate(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      liciensDate: error,
    }));
  };

  const handleDiagnosticCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDiagnosticCardNumber(newValue);

    const error = validateDiagnosticCardNumber(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      diagnosticCardNumber: error,
    }));
  };

  const handleValidityPeriodOfDCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValidityPeriodOfDC(newValue);

    const error = validateValidityPeriodOfDC(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      validityPeriodOfDC: error,
    }));
  };

  const handleTechnikChange = (techId: string) => {
    // Проверяем, что выбран действительный техник
    if (!techId) return;

    const selectedTechnik = technicians.find((tech) => tech.value === techId);
    if (selectedTechnik) {
      setTechnikId(techId);

      // Текст пункта — уже готовое ФИО техника (или логин, если имени нет)
      setTechnikName(selectedTechnik.text);

      // Если техник изменен, обновляем его на сервере
      if (techId !== previousTechnikId) {
        updateTechnik(techId);
      }
    }
  };

  const updateTechnik = async (techId: string) => {
    try {
      setIsUpdating(true);
      // Явное указание techId, а не null
      await TechnicalService.updateCarTechnik(car.id, techId);
      setPreviousTechnikId(techId);
      setAutonegotiation(false); // При изменении техника автосогласование отключается
    } catch (error) {
      console.error('Ошибка при обновлении техника:', error);
      alert('Ошибка при обновлении техника');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveTechnik = async () => {
    if (confirm('Вы уверены, что хотите удалить техника?')) {
      try {
        setIsUpdating(true);
        await TechnicalService.updateCarTechnik(car.id, null);
        setTechnikName('');
        setTechnikId('');
        setAutonegotiation(false);
        alert('Техник успешно удален');
      } catch (error) {
        console.error('Ошибка при удалении техника:', error);
        alert('Ошибка при удалении техника');
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const updateCar = async () => {
    const hasErrors = Object.values(errors).some((error) => error !== '');
    if (hasErrors) {
      alert('Пожалуйста, исправьте ошибки перед сохранением.');
      return;
    }

    const carData: CarUpdateData = {
      number: gosNumber,
      mark,
      model,
      taxiLicense: liciens,
      // Преобразуем даты в формат для бэкенда
      issueDate: toBackendDate(liciensDate),
      diagnosticCardNumber: diagnosticCardNumber ? parseInt(diagnosticCardNumber) : null,
      validityPeriodOfDC: validityPeriodOfDC ? toBackendDate(validityPeriodOfDC) : null,
      diagnosticCardVerified,
    };

    try {
      setIsUpdating(true);
      setUpdateError('');
      const updatedCar = await TechnicalService.updateCar(car.id, carData);

      // Обновляем локальные данные из ответа сервера
      if (updatedCar) {
        setLiciensDate(updatedCar.issueDate || liciensDate);
        setValidityPeriodOfDC(updatedCar.validityPeriodOfDC || validityPeriodOfDC);
        setDiagnosticCardNumber(updatedCar.diagnosticCardNumber?.toString() || '');
        setDiagnosticCardVerified(updatedCar.diagnosticCardVerified || false);

        // Сохраняем предыдущее состояние автосогласования
        if (updatedCar.autonegotiation !== undefined) {
          setAutonegotiation(updatedCar.autonegotiation);
        }

        // Проверяем и обновляем данные техника
        if (updatedCar.autonegotiationTechnik) {
          setTechnikId(updatedCar.autonegotiationTechnik.id);
          setTechnikName(fullName(updatedCar.autonegotiationTechnik));
        }
      }

      setPreviousDiagnosticCardNumber(diagnosticCardNumber);
      setPreviousValidityPeriodOfDC(validityPeriodOfDC);
      alert('Данные автомобиля успешно обновлены');
    } catch (error: any) {
      console.error('Ошибка при обновлении данных автомобиля:', error);
      setUpdateError(error.message || 'Ошибка при обновлении данных автомобиля');
      alert('Ошибка при обновлении данных автомобиля');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditToggle = () => {
    if (editable) {
      updateCar();
    }
    setEditable(!editable);
  };

  return (
    <div className="info__card" style={carLength == 1 ? undefined : { gridColumn: '1 / 3', marginBottom: '25px' }}>
      <div className="info__card-header">
        <div className="info__card-header__title">Данные автомобиля</div>
        <div className="info__card-header__descr" onClick={handleEditToggle}>
          {editable ? 'Сохранить' : 'Изменить'}
        </div>
      </div>
      <div className="info__card__wrapper" style={carLength == 1 ? undefined : { display: 'flex' }}>
        <div
          className="info__card-body"
          style={
            carLength == 1 ? undefined : { minWidth: '288px', borderRight: '1px solid #D3D3D3', paddingRight: '10px' }
          }
        >
          <Input
            label="Гос номер"
            id="gos-number"
            placeholder="Гос номер"
            value={gosNumber}
            onChange={handleGosNumberChange}
            required
            editable={editable}
          />
          {errors.gosNumber && <span className="error">{errors.gosNumber}</span>}

          <Input
            label="Марка"
            id="mark"
            placeholder="Марка"
            value={mark}
            onChange={handleMarkChange}
            required
            editable={editable}
          />
          {errors.mark && <span className="error">{errors.mark}</span>}

          <Input
            label="Модель"
            id="model"
            placeholder="Модель"
            value={model}
            onChange={handleModelChange}
            required
            editable={editable}
          />
          {errors.model && <span className="error">{errors.model}</span>}

          <Input
            label="Лицензия"
            id="liciens"
            placeholder="№123456"
            value={liciens}
            onChange={handleLiciensChange}
            required
            editable={editable}
          />
          {errors.liciens && <span className="error">{errors.liciens}</span>}

          <Input
            label="Дата выдачи лицензии"
            id="liciens-date"
            placeholder="Дата выдачи лицензии"
            value={formatDateForInput(liciensDate)}
            onChange={handleLiciensDateChange}
            required
            editable={editable}
            type="date"
          />
          {errors.liciensDate && <span className="error">{errors.liciensDate}</span>}

          {/* Новые поля для диагностической карты */}
          <div
            className="diagnostic-card-section"
            style={{ marginTop: '15px', borderTop: '1px solid #e0e0e0', paddingTop: '15px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ margin: 0 }}>Диагностическая карта</h4>
              {diagnosticCardVerified && (
                <div
                  style={{
                    marginLeft: '10px',
                    color: '#4CAF50',
                    fontSize: '20px',
                    width: '20px',
                    height: '20px',
                    background: '#4CAF50',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Проверено техником"
                >
                  <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 45%', minWidth: '120px' }}>
                <Input
                  label="Номер карты"
                  id="diagnostic-card-number"
                  placeholder="Введите номер"
                  value={diagnosticCardNumber}
                  onChange={handleDiagnosticCardNumberChange}
                  editable={editable}
                  type="number"
                />
                {errors.diagnosticCardNumber && <span className="error">{errors.diagnosticCardNumber}</span>}
              </div>

              <div style={{ flex: '1 1 45%', minWidth: '120px' }}>
                <Input
                  label="Срок действия"
                  id="validity-period"
                  placeholder="Срок действия"
                  value={formatDateForInput(validityPeriodOfDC)}
                  onChange={handleValidityPeriodOfDCChange}
                  editable={editable}
                  type="date"
                />
                {errors.validityPeriodOfDC && <span className="error">{errors.validityPeriodOfDC}</span>}
              </div>
            </div>

            {car.diagnosticCardPhoto && (
              <div style={{ marginTop: '15px' }}>
                <div style={{ fontWeight: 500, marginBottom: '8px' }}>Фото диагностической карты</div>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '10px',
                    background: '#fafafa',
                  }}
                >
                  <img
                    src={normalizeImageUrl(car.diagnosticCardPhoto)}
                    alt="Фото диагностической карты"
                    style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>Актуальное изображение</span>
                    <a
                      href={normalizeImageUrl(car.diagnosticCardPhoto)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#1E88E5',
                        textDecoration: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Открыть в полном размере
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Информация о технике */}
          {(autonegotiation || technikId) && (
            <div
              className="technik-section"
              style={{ marginTop: '15px', borderTop: '1px solid #e0e0e0', paddingTop: '15px' }}
            >
              <h4 style={{ margin: '0 0 10px 0' }}>Техник автосогласования ТО</h4>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {editable ? (
                  <div style={{ marginBottom: '10px' }}>
                    {isTechniciansLoading ? (
                      <div>Загрузка списка техников...</div>
                    ) : technicians.length > 0 ? (
                      <DropDownInput
                        items={technicians}
                        onSelect={handleTechnikChange}
                        initialValue={technikId}
                        editable={true}
                        label="ФИО техника"
                      />
                    ) : (
                      <div>Техники не найдены. Проверьте подключение к серверу.</div>
                    )}
                  </div>
                ) : (
                  <Input
                    label="ФИО техника"
                    id="technik-fullname"
                    placeholder="ФИО техника"
                    value={technikName || 'Не назначен'}
                    onChange={() => {}}
                    editable={false}
                  />
                )}

                {technikId && !editable && (
                  <button
                    onClick={handleRemoveTechnik}
                    disabled={isUpdating}
                    style={{
                      backgroundColor: '#ff4d4f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                      alignSelf: 'flex-start',
                      marginTop: '5px',
                    }}
                  >
                    Удалить
                  </button>
                )}
              </div>

              {technikId && (
                <div style={{ marginTop: '5px', fontSize: '14px', color: 'green' }}>Автосогласование активно</div>
              )}
            </div>
          )}

          {updateError && <div style={{ color: 'red', marginTop: '10px' }}>{updateError}</div>}
        </div>
        <ManyPhotoContainer carsPhoto={carPhotos} gosNumber={gosNumber} rotations={rotations} />
      </div>
    </div>
  );
}

export default AutoCard;
